import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SquaresFour,
  UsersThree,
  Buildings,
  Baby,
  ChalkboardTeacher,
  ChatCircleText,
  ClipboardText,
  CreditCard,
  FirstAid,
  Student,
  FileText,
  Folders,
  Image,
  ImagesSquare,
  Megaphone,
  Gear,
  Pulse,
  Confetti,
  CalendarCheck,
  CalendarBlank,
  ForkKnife,
  Sparkle,
  ShieldCheck,
  ChartLineUp,
  SignOut,
  CaretRight,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useOrganizationConfig } from "@/context/OrganizationConfigContext";
import useUnreadMessages from "@/lib/hooks/useUnreadMessages";
import { useBranding, BrandLogoChip } from "@/context/BrandingContext";
import { ROLES, ROLE_LABELS } from "@/lib/roles";
import NotificationBell from "@/components/NotificationBell";
import { GlobalSearchTriggerButton } from "@/components/GlobalSearchPalette";
import ErrorBoundary from "@/components/ErrorBoundary";
import SkipToMain from "@/components/a11y/SkipToMain";
import OrganizationSwitcher from "@/components/OrganizationSwitcher";

// ---------------------------------------------------------------------------
// Admin nav — grouped into pinned everyday actions + 5 collapsible accordions
// scoped by mental model. Each group's open/closed state persists per role
// via localStorage.
// ---------------------------------------------------------------------------
const ADMIN_PINNED = [
  { to: "/admin",               label: "Overview",      icon: SquaresFour, end: true },
  { to: "/admin/analytics",     label: "Analytics",     icon: ChartLineUp },
  { to: "/admin/messaging",     label: "Messaging",     icon: ChatCircleText, badge: "messaging", moduleKey: "messaging" },
  { to: "/admin/daily-reports", label: "Daily Reports", icon: ClipboardText, moduleKey: "daily_activity" },
  { to: "/admin/attendance",    label: "Attendance",    icon: CalendarCheck, moduleKey: "attendance" },
];

const ADMIN_GROUPS = [
  {
    key: "family",
    label: "Family & enrollment",
    defaultOpen: true,
    items: [
      { to: "/admin/admissions",  label: "Admissions", icon: Sparkle, moduleKey: "enrollment" },
      { to: "/admin/enrollment", label: "Pipeline", icon: Student, moduleKey: "enrollment" },
      { to: "/admin/inquiries",  label: "Inquiries",  icon: Sparkle, moduleKey: "enrollment" },
      { to: "/admin/families",   label: "Families",   icon: UsersThree },
      { to: "/admin/children",   label: "Children",   icon: Baby },
    ],
  },
  {
    key: "billing",
    label: "Billing & finance",
    defaultOpen: true,
    items: [
      { to: "/admin/billing", label: "Billing", icon: CreditCard, moduleKey: "family_billing" },
      { to: "/admin/billing/subsidies", label: "Subsidies & agencies", icon: ShieldCheck, moduleKey: "family_billing" },
    ],
  },
  {
    key: "trust",
    label: "Trust & safety",
    defaultOpen: true,
    items: [
      { to: "/admin/photos/moderation", label: "Photo Review", icon: ImagesSquare, moduleKey: "photos" },
      { to: "/admin/incidents",         label: "Incidents",    icon: FirstAid, moduleKey: "incidents" },
      { to: "/admin/compliance",        label: "Compliance",   icon: ShieldCheck },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    defaultOpen: false,
    items: [
      { to: "/admin/classrooms", label: "Classrooms", icon: ChalkboardTeacher },
      { to: "/admin/ratio-capacity", label: "Ratio & Capacity", icon: ChartLineUp, moduleKey: "ratio_capacity" },
      { to: "/admin/food-program", label: "Food program", icon: ForkKnife, moduleKey: "food_program" },
      { to: "/admin/staff",      label: "Staff",      icon: UsersThree },
      { to: "/admin/documents",  label: "Documents",  icon: Folders, moduleKey: "documents" },
      { to: "/admin/events",     label: "Events",     icon: CalendarBlank, moduleKey: "events" },
      { to: "/admin/conferences", label: "Conferences", icon: CalendarCheck, moduleKey: "conferences" },
      { to: "/admin/scheduling", label: "Scheduling", icon: CalendarCheck, moduleKey: "staffing" },
      { to: "/admin/time-off", label: "Time off", icon: CalendarBlank, moduleKey: "staffing" },
      { to: "/admin/learning", label: "Learning", icon: Sparkle, moduleKey: "learning" },
    ],
  },
  {
    key: "system",
    label: "System & settings",
    defaultOpen: false,
    items: [
      { to: "/admin/organizations",     label: "Organizations",     icon: Buildings },
      { to: "/admin/settings",          label: "Settings",          icon: Gear },
      { to: "/admin/system",            label: "System Health",     icon: Pulse },
      { to: "/admin/launch-rehearsal",  label: "Launch Rehearsal",  icon: Confetti },
    ],
  },
];

const CMS_PINNED = [
  { to: "/cms", label: "Overview", icon: SquaresFour, end: true },
];

const CMS_GROUPS = [
  {
    key: "content",
    label: "Content",
    defaultOpen: true,
    items: [
      { to: "/cms/pages",         label: "Pages",         icon: FileText, moduleKey: "public_site" },
      { to: "/cms/announcements", label: "Announcements", icon: Megaphone, moduleKey: "public_site" },
      { to: "/cms/media",         label: "Media Library", icon: Image, moduleKey: "public_site" },
    ],
  },
];

const NAV_STORAGE_KEY = "daycare-nav-groups-v1";

function readGroupState(roleKey) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(`${NAV_STORAGE_KEY}:${roleKey}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeGroupState(roleKey, state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${NAV_STORAGE_KEY}:${roleKey}`,
      JSON.stringify(state),
    );
  } catch {
    /* swallow — quota exceeded etc. */
  }
}

export default function DesktopShell() {
  const { profile, role, signOut } = useAuth();
  const { isModuleEnabled } = useOrganizationConfig();
  const unread = useUnreadMessages(profile?.id);
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const isCms = role === ROLES.CMS_EDITOR;
  const { pinned, groups } = useMemo(() => {
    const sourcePinned = isCms ? CMS_PINNED : ADMIN_PINNED;
    const sourceGroups = isCms ? CMS_GROUPS : ADMIN_GROUPS;
    const visible = (item) => !item.moduleKey || isModuleEnabled(item.moduleKey);
    return {
      pinned: sourcePinned.filter(visible),
      groups: sourceGroups
        .map((group) => ({ ...group, items: group.items.filter(visible) }))
        .filter((group) => group.items.length > 0),
    };
  }, [isCms, isModuleEnabled]);
  const roleKey = isCms ? "cms" : "admin";
  const mobileNavItems = [...pinned, ...groups.flatMap((group) => group.items)];

  const [openMap, setOpenMap] = useState(() => {
    const persisted = readGroupState(roleKey);
    const initial = {};
    for (const g of groups) {
      initial[g.key] = persisted[g.key] ?? g.defaultOpen;
    }
    return initial;
  });

  const toggleGroup = useCallback((key) => {
    setOpenMap((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writeGroupState(roleKey, next);
      return next;
    });
  }, [roleKey]);

  // If the role flips (e.g., admin <-> cms in dev), re-hydrate.
  useEffect(() => {
    const persisted = readGroupState(roleKey);
    setOpenMap(() => {
      const fresh = {};
      for (const g of groups) {
        fresh[g.key] = persisted[g.key] ?? g.defaultOpen;
      }
      return fresh;
    });
  }, [roleKey, groups]);

  // Keep the active route discoverable when a user arrives via a deep link,
  // refresh, or a notification. Persisted preferences still control all
  // unrelated groups.
  useEffect(() => {
    const activeGroup = groups.find((group) =>
      group.items.some((item) =>
        location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
      ),
    );
    if (!activeGroup) return;
    setOpenMap((prev) => {
      if (prev[activeGroup.key]) return prev;
      const next = { ...prev, [activeGroup.key]: true };
      writeGroupState(roleKey, next);
      return next;
    });
  }, [groups, location.pathname, roleKey]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-background text-neutral-900"
      data-testid="desktop-shell"
      style={branding.cssVars}
    >
      <SkipToMain targetId="main" />
      <aside
        data-testid="desktop-sidebar"
        role="complementary"
        aria-label="Primary navigation region"
        className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-neutral-200 bg-white px-5 py-8 md:flex"
      >
        <div
          data-testid="desktop-brand"
          className="mb-8 flex items-center gap-3"
        >
          <BrandLogoChip size={40} testId="desktop-brand-logo" />
          <div className="flex min-w-0 flex-col leading-tight">
            <span
              data-testid="desktop-brand-name"
              className="truncate font-heading text-base font-semibold tracking-tight text-neutral-900"
            >
              {branding.displayName}
            </span>
            <span
              className="overline"
              style={{ color: branding.primary }}
            >
              {ROLE_LABELS[role]}
            </span>
            <OrganizationSwitcher className="mt-2" />
          </div>
        </div>

        {/* Module H · global search palette trigger — sits between
            brand and nav so it's always one tap away. */}
        <div className="mb-4">
          <GlobalSearchTriggerButton />
        </div>

        <nav
          className="flex-1 space-y-4 overflow-y-auto pr-1"
          data-testid="desktop-nav"
          aria-label="Primary"
        >
          {/* Pinned everyday actions — always visible, no group header. */}
          <ul className="space-y-1">
            {pinned.map((item) => (
              <li key={item.to}>
                <NavItem item={item} unread={unread} />
              </li>
            ))}
          </ul>

          {/* Collapsible groups */}
          {groups.map((group) => (
            <NavGroup
              key={group.key}
              group={group}
              open={!!openMap[group.key]}
              onToggle={() => toggleGroup(group.key)}
              unread={unread}
            />
          ))}
        </nav>

        <div className="border-t border-neutral-200 pt-4">
          <div className="mb-3 flex items-center gap-3 px-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-sm font-semibold text-neutral-700">
              {(profile?.full_name || profile?.email || "?")
                .slice(0, 1)
                .toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-neutral-900">
                {profile?.full_name || "—"}
              </p>
              <p className="truncate text-xs text-neutral-500">
                {profile?.email}
              </p>
            </div>
            <NotificationBell variant="desktop" />
          </div>
          <button
            data-testid="desktop-signout-button"
            onClick={handleSignOut}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-800 transition-all hover:bg-neutral-200 active:scale-[0.98]"
          >
            <SignOut size={16} weight="duotone" />
            Sign out
          </button>
        </div>
      </aside>

      <header
        data-testid="mobile-admin-header"
        className="sticky top-0 z-40 border-b border-neutral-200 bg-white/95 shadow-sm backdrop-blur-xl md:hidden"
      >
        <div className="pt-safe-header flex items-center justify-between gap-3 px-4 pb-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <BrandLogoChip size={34} testId="mobile-admin-brand-logo" />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-[10px] font-medium uppercase tracking-[0.15em] text-primary-hover">
                {ROLE_LABELS[role]}
              </p>
              <p className="truncate font-heading text-sm font-semibold text-neutral-900">
                {branding.displayName}
              </p>
              <p className="truncate text-[11px] text-neutral-500">
                {profile?.full_name || profile?.email}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <NotificationBell variant="mobile" />
            <button
              type="button"
              data-testid="mobile-admin-signout-button"
              onClick={handleSignOut}
              aria-label="Sign out"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 transition hover:bg-neutral-50"
            >
              <SignOut size={17} weight="duotone" />
            </button>
          </div>
        </div>
        <nav
          data-testid="mobile-admin-nav"
          aria-label="Primary"
          className="border-t border-neutral-100 px-3 py-2"
        >
          <ul className="flex gap-1.5 overflow-x-auto pb-1">
            {mobileNavItems.map((item) => (
              <li key={item.to} className="shrink-0">
                <MobileNavItem item={item} unread={unread} />
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main
        id="main"
        data-testid="desktop-main"
        role="main"
        tabIndex={-1}
        className="min-h-screen md:pl-64"
      >
        <div className="mx-auto w-full max-w-7xl px-6 py-8 md:px-12 md:py-12">
          <ErrorBoundary scope={role === ROLES.CMS_EDITOR ? "cms" : "admin"}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}

function NavGroup({ group, open, onToggle, unread }) {
  return (
    <div data-testid={`desktop-nav-group-${group.key}`} data-open={open}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`desktop-nav-group-list-${group.key}`}
        data-testid={`desktop-nav-group-${group.key}-toggle`}
        className="overline flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-neutral-500 transition hover:text-neutral-900"
      >
        <span>{group.label}</span>
        <CaretRight
          size={11}
          weight="bold"
          aria-hidden
          className={`text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <ul
          id={`desktop-nav-group-list-${group.key}`}
          className="mt-1 space-y-1"
        >
          {group.items.map((item) => (
            <li key={item.to}>
              <NavItem item={item} unread={unread} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NavItem({ item, unread }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`desktop-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
          isActive
            ? "bg-primary/10 text-primary-hover"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={20} weight={isActive ? "fill" : "duotone"} aria-hidden />
          <span className="flex-1">{item.label}</span>
          {item.badge === "messaging" && unread > 0 && (
            <span
              data-testid="desktop-nav-unread-badge"
              data-count={unread}
              className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1.5 text-[10px] font-bold leading-none text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function MobileNavItem({ item, unread }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`mobile-admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition ${
          isActive
            ? "border-primary/40 bg-primary/10 text-primary-hover"
            : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={15} weight={isActive ? "fill" : "duotone"} aria-hidden />
          <span>{item.label}</span>
          {item.badge === "messaging" && unread > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold leading-none text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
